import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ReportFilters, SearchQuery, FacetsResult } from '../hooks/useReports';
import LocationSearchInput from './LocationSearchInput';

interface AdvancedSearchFilterProps {
  onSearch: (query: SearchQuery) => void;
  onFilter: (filters: ReportFilters) => void;
  onFacetedSearch: (filters: Omit<ReportFilters, 'startAfter' | 'limit'>) => void;
  facets?: FacetsResult['facets'];
  searchMetadata?: {
    query: string;
    resultsCount: number;
    searchTime: number;
  };
  isLoading?: boolean;
  isAdmin?: boolean;

}

// Category data for filter options
const categories = [
  { id: 'all', name: 'All', icon: '🏠', tag: '' },
  { id: 'furniture', name: 'Furniture', icon: '🪑', tag: 'furniture' },
  { id: 'electronics', name: 'Electronics', icon: '📱', tag: 'electronics' },
  { id: 'books', name: 'Books', icon: '📚', tag: 'books' },
  { id: 'clothing', name: 'Clothing', icon: '👕', tag: 'clothing' },
  { id: 'appliances', name: 'Appliances', icon: '🔌', tag: 'appliances' },
  { id: 'toys', name: 'Toys', icon: '🧸', tag: 'toys' },
  { id: 'garden', name: 'Garden', icon: '🌱', tag: 'garden' },
  { id: 'sports', name: 'Sports', icon: '⚽', tag: 'sports' },
  { id: 'art', name: 'Art', icon: '🎨', tag: 'art' },
  { id: 'tools', name: 'Tools', icon: '🔧', tag: 'tools' },
  { id: 'kitchen', name: 'Kitchen', icon: '🍳', tag: 'kitchen' }
];

const statusOptions = [
  { value: '', label: 'Any Status' },
  { value: 'active', label: 'Active' },
  { value: 'going_soon', label: 'Going Soon' },
  { value: 'gone', label: 'Gone' }
];

const AdvancedSearchFilter: React.FC<AdvancedSearchFilterProps> = ({
  onSearch,
  onFilter,
  onFacetedSearch,
  facets,
  searchMetadata,
  isLoading = false
}) => {
  // State management
  const [showFilters, setShowFilters] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  // Search and filter state
  const [searchText, setSearchText] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  // Advanced filters
  const [filters, setFilters] = useState<ReportFilters>({
    status: '',
    suburb: '',
    council: '',
    postcode: '',
    tags: [],
    categories: [],
    dateFrom: undefined,
    dateTo: undefined,
    sortBy: 'submittedAt',
    sortOrder: 'desc',
    limit: 20
  });

  // Refs
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);

  // Close filter panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(event.target as Node) &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle search submission
  const handleSearch = useCallback(() => {
    const searchQuery: SearchQuery = {
      text: searchText.trim() || undefined,
      status: selectedStatus || undefined,
      postcode: filters.postcode || undefined,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
      categories: selectedCategories.length > 0 ? selectedCategories : undefined,
      dateFrom: dateRange.from ? new Date(dateRange.from) : undefined,
      dateTo: dateRange.to ? new Date(dateRange.to) : undefined,
      sortBy: 'relevance',
      sortOrder: 'desc',
      limit: 20
    };

    if (searchText.trim()) {
      onSearch(searchQuery);
    } else {
      // If no search text, use regular filtering
      handleApplyFilters();
    }
  }, [searchText, selectedStatus, selectedTags, selectedCategories, dateRange, filters.postcode, onSearch]);

  // Handle filter application
  const handleApplyFilters = useCallback(() => {
    const activeFilters: ReportFilters = {
      status: selectedStatus || undefined,
      postcode: filters.postcode || undefined,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
      categories: selectedCategories.length > 0 ? selectedCategories : undefined,
      dateFrom: dateRange.from ? new Date(dateRange.from) : undefined,
      dateTo: dateRange.to ? new Date(dateRange.to) : undefined,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
      limit: 20
    };

    onFilter(activeFilters);
    setShowFilters(false);
    setIsMobileFilterOpen(false);
  }, [selectedStatus, selectedTags, selectedCategories, dateRange, filters, onFilter]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setSearchText('');
    setSelectedLocation('');
    setSelectedCategory('');
    setSelectedStatus('');
    setSelectedTags([]);
    setSelectedCategories([]);
    setDateRange({ from: '', to: '' });
    setFilters({
      status: '',
      suburb: '',
      council: '',
      postcode: '',
      tags: [],
      categories: [],
      dateFrom: undefined,
      dateTo: undefined,
      sortBy: 'submittedAt',
      sortOrder: 'desc',
      limit: 20
    });
    onFilter({});
  }, [onFilter]);

  // Handle category selection
  const handleCategorySelect = useCallback((category: typeof categories[0]) => {
    setSelectedCategory(category.id);
    if (category.tag) {
      setSelectedCategories(prev =>
        prev.includes(category.name) ? prev : [...prev, category.name]
      );
    } else {
      setSelectedCategories([]);
    }
  }, []);

  // Handle tag removal
  const handleRemoveTag = useCallback((tagToRemove: string) => {
    setSelectedTags(prev => prev.filter(tag => tag !== tagToRemove));
    // Also clear category if it matches
    const matchingCategory = categories.find(cat => cat.tag === tagToRemove);
    if (matchingCategory && selectedCategory === matchingCategory.id) {
      setSelectedCategory('');
    }
  }, [selectedCategory]);

  // Handle category toggle
  const handleCategoryToggle = useCallback((category: typeof categories[0]) => {
    const isSelected = selectedCategories.includes(category.name);
    
    if (isSelected) {
      // Remove category
      setSelectedCategories(prev => prev.filter(cat => cat !== category.name));
      if (selectedCategory === category.id) {
        setSelectedCategory('');
      }
    } else {
      // Add category
      setSelectedCategories(prev => [...prev, category.name]);
      setSelectedCategory(category.id);
    }
  }, [selectedCategories, selectedCategory]);

  // Handle Enter key in search
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  // Get active filter count for display
  const getActiveFilterCount = useCallback(() => {
    let count = 0;
    if (selectedStatus) count++;
    if (selectedTags.length > 0) count += selectedTags.length;
    if (selectedCategories.length > 0) count += selectedCategories.length;
    if (dateRange.from || dateRange.to) count++;
    if (filters.postcode) count++;
    return count;
  }, [selectedStatus, selectedTags, selectedCategories, dateRange, filters.postcode]);

  return (
    <div className="relative">
      {/* Main Search Container - Modern Style */}
      <div
        ref={searchContainerRef}
        className="bg-white rounded-full shadow-lg border border-gray-200 transition-all duration-300 mx-auto max-w-4xl"
      >
        <div className="flex items-center p-2">
          {/* Location Section */}
          <div className="flex-1 px-4 py-3 border-r border-gray-200">
            <div className="text-xs font-semibold text-urban mb-1">Where</div>
            <LocationSearchInput
              onLocationSelect={(locationDetails) => {
                setSelectedLocation(locationDetails.displayText);
                setFilters(prev => ({
                  ...prev,
                  postcode: locationDetails.postcode || '',
                  suburb: locationDetails.suburb || '',
                  council: locationDetails.council || '',
                  latitude: locationDetails.latitude,
                  longitude: locationDetails.longitude
                }));
              }}
              placeholder="Search by suburb or postcode"
              initialValue={selectedLocation}
              disabled={isLoading}
              className="w-full bg-transparent border-none outline-none text-sm text-urban placeholder-gray-400"
            />
          </div>

          {/* Category Section */}
          <div className="flex-1 px-4 py-3 border-r border-gray-200">
            <div className="text-xs font-semibold text-urban mb-1">Category</div>
            <select
              value={selectedCategory}
              onChange={(e) => {
                const category = categories.find(cat => cat.id === e.target.value);
                if (category) handleCategorySelect(category);
              }}
              className="w-full bg-transparent border-none outline-none text-sm text-urban"
              disabled={isLoading}
            >
              <option value="">Any category</option>
              {categories.slice(1).map(category => (
                <option key={category.id} value={category.id}>
                  {category.icon} {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Search Text Section */}
          <div className="flex-1 px-4 py-3">
            <div className="text-xs font-semibold text-urban mb-1">Search</div>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search treasures..."
              className="w-full bg-transparent border-none outline-none text-sm text-urban placeholder-gray-400"
              disabled={isLoading}
            />
          </div>

          {/* Search Button */}
          <button
            onClick={handleSearch}
            disabled={isLoading}
            className="bg-cardboard hover:bg-cardboard-600 text-white rounded-full w-12 h-12 flex items-center justify-center transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Search"
          >
            {isLoading ? (
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
          </button>

          {/* Filters Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`
              ml-2 px-4 py-2 rounded-full border transition-all duration-200 flex items-center gap-2
              ${showFilters
                ? 'bg-urban text-white border-urban'
                : 'bg-white text-urban border-gray-300 hover:border-cardboard'
              }
            `}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
            </svg>
            <span className="text-sm font-medium">Filters</span>
            {getActiveFilterCount() > 0 && (
              <span className="bg-cardboard text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {getActiveFilterCount()}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filter Panel - Desktop */}
      {showFilters && (
        <div
          ref={filterPanelRef}
          className="hidden md:block absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 w-full max-w-4xl z-50 max-h-[80vh] overflow-y-auto"
        >
          <FilterPanelContent
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedTags={selectedTags}
            handleRemoveTag={handleRemoveTag}
            selectedCategories={selectedCategories}
            handleCategoryToggle={handleCategoryToggle}
            dateRange={dateRange}
            setDateRange={setDateRange}
            setFilters={setFilters}
            facets={facets}
            isLoading={isLoading}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            onFacetedSearch={onFacetedSearch}
          />
        </div>
      )}

      {/* Mobile Filter Drawer */}
      {isMobileFilterOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50">
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-urban">Filters</h3>
              <button
                onClick={() => setIsMobileFilterOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <FilterPanelContent
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
                selectedTags={selectedTags}
                handleRemoveTag={handleRemoveTag}
                selectedCategories={selectedCategories}
                handleCategoryToggle={handleCategoryToggle}
                dateRange={dateRange}
                setDateRange={setDateRange}
                setFilters={setFilters}
                facets={facets}
                isLoading={isLoading}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
                onFacetedSearch={onFacetedSearch}
              />
            </div>
          </div>
        </div>
      )}

      {/* Mobile Filters Button */}
      <div className="md:hidden mt-4 flex justify-center">
        <button
          onClick={() => setIsMobileFilterOpen(true)}
          className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-300 rounded-full shadow-sm hover:shadow-md transition-shadow"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
          </svg>
          <span className="font-medium text-urban">Filters</span>
          {getActiveFilterCount() > 0 && (
            <span className="bg-cardboard text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {getActiveFilterCount()}
            </span>
          )}
        </button>
      </div>

      {/* Active Filter Chips */}
      {(selectedTags.length > 0 || selectedStatus || dateRange.from || dateRange.to) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {selectedStatus && (
            <FilterChip
              label={`Status: ${statusOptions.find(s => s.value === selectedStatus)?.label}`}
              onRemove={() => setSelectedStatus('')}
            />
          )}
          {selectedTags.map(tag => (
            <FilterChip
              key={tag}
              label={tag}
              onRemove={() => handleRemoveTag(tag)}
            />
          ))}
          {(dateRange.from || dateRange.to) && (
            <FilterChip
              label={`Date: ${dateRange.from || 'Any'} - ${dateRange.to || 'Any'}`}
              onRemove={() => setDateRange({ from: '', to: '' })}
            />
          )}
        </div>
      )}

      {/* Search Results Metadata */}
      {searchMetadata && (
        <div className="mt-4 text-sm text-gray-600 text-center">
          Found {searchMetadata.resultsCount} results for "{searchMetadata.query}"
          in {searchMetadata.searchTime}ms
        </div>
      )}
    </div>
  );
};

// Filter Panel Content Component
interface FilterPanelContentProps {
  selectedStatus: string;
  setSelectedStatus: (status: string) => void;
  selectedTags: string[];
  handleRemoveTag: (tag: string) => void;
  selectedCategories: string[];
  handleCategoryToggle: (category: typeof categories[0]) => void;
  dateRange: { from: string; to: string };
  setDateRange: (range: { from: string; to: string }) => void;
  setFilters: (filters: ReportFilters | ((prev: ReportFilters) => ReportFilters)) => void;
  facets?: FacetsResult['facets'];
  isLoading: boolean;
  onApply: () => void;
  onClear: () => void;
  onFacetedSearch: (filters: Omit<ReportFilters, 'startAfter' | 'limit'>) => void;
}

const FilterPanelContent: React.FC<FilterPanelContentProps> = ({
  selectedStatus,
  setSelectedStatus,
  selectedTags,
  // @ts-ignore - handleRemoveTag is used in the component but TS doesn't detect it
  handleRemoveTag,
  selectedCategories,
  handleCategoryToggle,
  dateRange,
  setDateRange,
  setFilters,
  facets,
  isLoading,
  onApply,
  onClear,
  onFacetedSearch
}) => {
  return (
    <>
      {/* Status Filter */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-urban mb-4">Status</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {statusOptions.map(option => (
            <button
              key={option.value}
              onClick={() => setSelectedStatus(option.value)}
              className={`
                p-3 rounded-lg border text-center transition-all duration-200
                ${selectedStatus === option.value
                  ? 'bg-cardboard border-cardboard text-white'
                  : 'bg-white border-gray-300 text-urban hover:border-cardboard'
                }
              `}
              disabled={isLoading}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Category Tags */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-urban mb-4">Categories</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {categories.slice(1).map(category => (
            <button
              key={category.id}
              onClick={() => handleCategoryToggle(category)}
              className={`
                p-3 rounded-lg border text-center transition-all duration-200 flex flex-col items-center gap-2
                ${selectedCategories.includes(category.name)
                  ? 'bg-cardboard border-cardboard text-white'
                  : 'bg-white border-gray-300 text-urban hover:border-cardboard'
                }
              `}
              disabled={isLoading}
            >
              <span className="text-xl">{category.icon}</span>
              <span className="text-xs font-medium">{category.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Date Range */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-urban mb-4">Date Range</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-urban mb-2">From</label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-cardboard/20"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-urban mb-2">To</label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-cardboard/20"
              disabled={isLoading}
            />
          </div>
        </div>
      </div>

      {/* Facets Display */}
      {facets && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-urban mb-4">Popular Filters</h4>

          {/* Popular Tags */}
          {Object.keys(facets.tags).length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm font-medium text-urban mb-2">Popular Tags</h5>
              <div className="flex flex-wrap gap-2">
                {Object.entries(facets.tags)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 10)
                  .map(([tag, count]) => (
                    <button
                      key={tag}
                      onClick={() => {
                        if (!selectedTags.includes(tag)) {
                          // Add tag logic
                        }
                      }}
                      className="px-3 py-1 text-sm bg-gray-100 hover:bg-cardboard hover:text-white rounded-full transition-colors"
                      disabled={isLoading}
                    >
                      {tag} ({count})
                    </button>
                  ))}
              </div>
            </div>
          )}

          {/* Popular Postcodes */}
          {Object.keys(facets.postcodes).length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm font-medium text-urban mb-2">Popular Areas</h5>
              <div className="flex flex-wrap gap-2">
                {Object.entries(facets.postcodes)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 8)
                  .map(([postcode, count]) => (
                    <button
                      key={postcode}
                      onClick={() => setFilters(prev => ({ ...prev, postcode }))}
                      className="px-3 py-1 text-sm bg-gray-100 hover:bg-cardboard hover:text-white rounded-full transition-colors"
                      disabled={isLoading}
                    >
                      {postcode} ({count})
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
        <button
          onClick={onClear}
          disabled={isLoading}
          className="flex-1 px-6 py-3 bg-white border border-gray-300 text-urban rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Clear All
        </button>
        <button
          onClick={() => onFacetedSearch({})}
          disabled={isLoading}
          className="flex-1 px-6 py-3 bg-verge text-white rounded-lg hover:bg-verge-600 transition-colors disabled:opacity-50"
        >
          Show Popular
        </button>
        <button
          onClick={onApply}
          disabled={isLoading}
          className="flex-1 px-6 py-3 bg-urban text-white rounded-lg hover:bg-urban-600 transition-colors disabled:opacity-50"
        >
          Apply Filters
        </button>
      </div>
    </>
  );
};

// Filter Chip Component
interface FilterChipProps {
  label: string;
  onRemove: () => void;
}

const FilterChip: React.FC<FilterChipProps> = ({ label, onRemove }) => {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1 bg-verge text-white rounded-full text-sm">
      <span>{label}</span>
      <button
        onClick={onRemove}
        className="hover:bg-verge-600 rounded-full p-0.5 transition-colors"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default AdvancedSearchFilter;